import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { X, QrCode, CheckCircle } from 'lucide-react';

// Full-screen camera scanner for bulk dispatch handovers.
// onScan(decodedText) is called with a cooldown between reads;
// return value of onScan is ignored — parent decides what to do.
export const DriverQrScanner = ({ onScan, onClose, acceptedCount = 0 }) => {
  const scannerRef = useRef(null);
  const lastScanRef = useRef({ text: '', at: 0 });
  const [error, setError] = useState('');
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    const scanner = new Html5Qrcode('driver-qr-reader');
    scannerRef.current = scanner;
    let cancelled = false;
    // Defer start past React StrictMode's dev double-mount so the first
    // (immediately unmounted) instance never touches the camera.
    const timer = setTimeout(() => {
      if (cancelled) return;
      scanner
      .start(
        { facingMode: 'environment' },
        {
          fps: 10,
          // Bigger scan box (70% of the viewfinder) so close-range slips decode faster
          qrbox: (vw, vh) => {
            const s = Math.floor(Math.min(vw, vh) * 0.7);
            return { width: s, height: s };
          },
          // Request a high-res stream — html5-qrcode's 640x480 default is
          // what makes iPhones look blurry and fail to decode.
          videoConstraints: {
            facingMode: 'environment',
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          // Native BarcodeDetector (when available) decodes faster & more reliably
          experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        },
        (decodedText) => {
          const now = Date.now();
          // 2.5s cooldown + ignore immediate duplicate reads
          if (now - lastScanRef.current.at < 2500 && lastScanRef.current.text === decodedText) return;
          if (now - lastScanRef.current.at < 1200) return;
          lastScanRef.current = { text: decodedText, at: now };
          setFlash(true);
          setTimeout(() => setFlash(false), 400);
          if (navigator.vibrate) navigator.vibrate(80);
          onScan(decodedText);
        },
        () => {}
      )
      .then(() => {
        // stop immediately if the component unmounted while starting
        if (cancelled) {
          try { scanner.stop().catch(() => {}); } catch { /* noop */ }
          return;
        }
        // iOS blur fix: once the stream is live, request continuous autofocus.
        // NOTE: no zoom — forced zoom made the view far too tight to scan.
        // Constraints must be applied AFTER start — iOS ignores them otherwise.
        setTimeout(() => {
          if (cancelled) return;
          try {
            const caps = scanner.getRunningTrackCapabilities ? (scanner.getRunningTrackCapabilities() || {}) : {};
            if (Array.isArray(caps.focusMode) && caps.focusMode.includes('continuous')) {
              scanner.applyVideoConstraints({
                focusMode: 'continuous',
                advanced: [{ focusMode: 'continuous' }],
              }).catch(() => {});
            }
            // Reset any zoom the browser may have carried over from a previous session
            if (caps.zoom && caps.zoom.min != null) {
              scanner.applyVideoConstraints({ advanced: [{ zoom: caps.zoom.min }] }).catch(() => {});
            }
          } catch { /* noop */ }
        }, 1200);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          String(err).includes('NotAllowed')
            ? 'Camera permission denied. Allow camera access in your browser settings and try again.'
            : 'Could not start the camera on this device.'
        );
      });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      try {
        if (scanner.isScanning) {
          scanner.stop().then(() => { try { scanner.clear(); } catch { /* noop */ } }).catch(() => {});
        } else {
          try { scanner.clear(); } catch { /* noop */ }
        }
      } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col" data-testid="driver-qr-scanner">
      <div className="flex items-center justify-between p-4 bg-wt-surface border-b border-wt-border">
        <div className="flex items-center gap-2 text-wt-gold font-bold">
          <QrCode className="w-5 h-5" />
          Scan Packing Slip
        </div>
        <button onClick={onClose} className="p-2 rounded-xl bg-wt-bg" data-testid="scanner-close-btn">
          <X className="w-6 h-6 text-wt-text-secondary" />
        </button>
      </div>
      <div className={`flex-1 relative bg-black ${flash ? 'ring-8 ring-inset ring-green-500' : ''}`}>
        {/* object-contain: show the FULL camera frame — object-cover cropped it so hard it looked zoomed-in */}
        <div id="driver-qr-reader" className="w-full h-full [&_video]:object-contain [&_video]:h-full [&_video]:w-full" />
        {error && (
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <p className="text-center text-red-400 text-sm bg-wt-surface border border-red-500/40 rounded-xl p-4" data-testid="scanner-error">
              {error}
            </p>
          </div>
        )}
      </div>
      <div className="p-4 bg-wt-surface border-t border-wt-border text-center">
        {acceptedCount > 0 ? (
          <p className="inline-flex items-center gap-2 text-green-400 font-semibold" data-testid="scanner-accepted-count">
            <CheckCircle className="w-5 h-5" /> {acceptedCount} order{acceptedCount === 1 ? '' : 's'} accepted this session
          </p>
        ) : (
          <p className="text-sm text-wt-text-muted">Point the camera at the QR on a packing slip — assigned orders are accepted automatically</p>
        )}
      </div>
    </div>
  );
};

export default DriverQrScanner;
